import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl, statusList } from "../../../comman/config";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// Add/Edit Department. A department is one collapsible group in the side bar and the screens that
// hang under it.
//
// The screen dropdown offers active screens only, plus whatever this department already holds --
// a screen that was disabled after being added stays in the list rather than silently dropping
// out of the department the next time somebody saves an unrelated change to it.
export default function DepartmentMasterForm({ detail, page = 'department_master' }) {

    const [formData, setFormData] = useState({
        name: '',
        screens: [],
        status: 1,
    });
    const [screenOptions, setScreenOptions] = useState([]);
    const [validationErrors, setValidationErrors] = useState({
        name: false,
        screens: false,
    });
    const [saveError, setSaveError] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const formRef = useRef(null);

    const navigate = useNavigate();

    // Two screens can share a name -- Order, Receipt and Issue each appear in more than one store
    // -- so the option carries the path as well. Without it the dropdown offers two entries called
    // Order with nothing to choose between them.
    const toOption = (screen) => ({ value: screen.id, label: `${screen.name} (${screen.path})` });

    useEffect(() => {
        const getScreenList = async () => {
            try {
                const response = await axios.get(`${baseUrl}/screen_master/list`);
                const screens = response.data?.data ?? [];
                const attached = detail ? (detail[0].screens ?? []).map((screen) => screen.id) : [];
                setScreenOptions(
                    screens
                        .filter((screen) => Number(screen.status) === 1 || attached.includes(screen.id))
                        .map(toOption)
                );
            } catch (error) {
                console.log(error);
            }
        };
        getScreenList();
    }, [detail]);

    useEffect(() => {
        if (detail) {
            const department = detail[0];
            setFormData({
                name: department.name || '',
                screens: (department.screens ?? []).map(toOption),
                status: Number(department.status ?? 1),
            });
        }
    }, [detail]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newValidationErrors = {
            name: formData.name.trim() === '',
            screens: formData.screens.length === 0,
        };
        setValidationErrors(newValidationErrors);
        const hasErrors = Object.values(newValidationErrors).some((error) => error);

        if (hasErrors) {
            const firstInvalidInput = formRef.current.querySelector('.invalid');
            if (firstInvalidInput) {
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaveError('');
        try {
            setPopupOpen(true);
            const url = detail ? `${page}/edit/${detail[0].id}` : `${page}/add`;
            await axios.post(`${baseUrl}/${url}`, {
                formData: {
                    ...formData,
                    // The order they were picked in is the order they appear under the heading.
                    screens: formData.screens.map((screen) => screen.value),
                },
            });
            setPopupOpen(false);
            navigate(`/${page}/list`);
        } catch (error) {
            setPopupOpen(false);
            console.log(error);
            setSaveError(error?.response?.data?.msg ?? 'Could not save the department.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        setValidationErrors((prevErrors) => ({
            ...prevErrors,
            [name]: false
        }));
    };

    return (
        <form ref={formRef}>
            <div className="space-y-12">
                <div>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
                        <div className="sm:col-span-2">
                            <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Department Name *</label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    placeholder="Department Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`block w-full rounded-md border ${validationErrors.name ? 'border-red-500 invalid' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                                />
                            </div>
                            {validationErrors.name && <div className="text-red-500">Please enter a department name.</div>}
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="screens" className="block text-sm font-medium leading-6 text-gray-900">Screen *</label>
                            <div className={`mt-2 ${validationErrors.screens ? 'invalid' : ''}`}>
                                <Select
                                    {...menuAboveTable}
                                    isMulti
                                    inputId="screens"
                                    classNamePrefix="select"
                                    className="basic-multi-select"
                                    value={formData.screens}
                                    options={screenOptions}
                                    onChange={(selected) => {
                                        setFormData((prevData) => ({ ...prevData, screens: selected ?? [] }));
                                        setValidationErrors((prevErrors) => ({ ...prevErrors, screens: false }));
                                    }}
                                    placeholder="Select screens"
                                />
                            </div>
                            {/* No hint under the field. It used to say the screens appear in the
                                order they are picked, which is true of the menu but is not what
                                this field is for: it binds screens to a department, so that a
                                department's screens are known. */}
                            {validationErrors.screens
                                && <div className="text-red-500">Please select at least one screen.</div>}
                        </div>


                        <div className="sm:col-span-2">
                            <label htmlFor="status" className="block text-sm font-medium leading-6 text-gray-900">Status *</label>
                            <div className="mt-2">
                                <Select
                                    {...menuAboveTable}
                                    inputId="status"
                                    classNamePrefix="select"
                                    value={statusList.find((option) => option.value === Number(formData.status))}
                                    options={statusList}
                                    onChange={(selected) => setFormData((prevData) => ({ ...prevData, status: selected.value }))}
                                    isClearable={false}
                                />
                            </div>
                        </div>
                    </div>
                    {saveError && <div className="text-red-500 mt-4">{saveError}</div>}
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
                <Link to={`/${page}/list`}>
                    <button type="button" className="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
                </Link>
                <button
                    onClick={handleSubmit}
                    disabled={popupOpen}
                    type="submit"
                    className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                    Submit
                </button>
            </div>
        </form>
    )
}
